import { Box, Button, Container, Divider, Title } from '@mantine/core';
import { joiResolver, useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import React, { useState } from 'react';
import { Check, X } from 'tabler-icons-react';
import { OpenComponent } from '../../../../enums/misc.enum';
import { Roles } from '../../../../enums/roles-enum';
import useAuth from '../../../../hooks/useAuth';
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate';
import {
  CreatePartnerDetailsForm,
  CreatePartnersRequest,
  UserDetailsForm,
} from '../../../../type/entity.types';
import { ErrorResponse } from '../../../../type/error-response.types';
import BackButton from '../../../buttons/back-button.component';
import PartnerResellerUserDetails from '../components/partner-reseller-user-details.component';
import { CreatePartnerSchema } from '../schemas/create-partner.schema';
import { UserDetailsSchema } from '../schemas/shared-create-entity.schema';
import PartnerDetails from './partner-details.component';

function CreatePartnerEntity(props: Props) {
  const { setComponentManager, setRefreshTable } = props;
  const [isLoading, setIsLoading] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();

  const form = useForm<CreatePartnerDetailsForm>({
    initialValues: {
      entityName: '',
      email: '',
      MDR: '0',
    },
    schema: joiResolver(CreatePartnerSchema),
  });

  const userDetailsForm = useForm<UserDetailsForm>({
    initialValues: {
      firstName: '',
      middleName: '',
      noMiddleName: false,
      lastName: '',
      username: '',
      password: '',
      confirmPassword: '',
      userEmail: '',
    },
    schema: joiResolver(UserDetailsSchema),
  });

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    const formHasErrors = form.validate().hasErrors;
    const userFormHasErrors = userDetailsForm.validate().hasErrors;

    if (formHasErrors || userFormHasErrors) {
      return;
    }
    const values = { ...form.values, ...userDetailsForm.values };

    const request: CreatePartnersRequest = {
      partnerName: values.entityName,
      MDR: values.MDR,

      contact: {
        email: values.email,
      },

      user: {
        firstName: values.firstName,
        middleName: values.middleName ?? '.',
        lastName: values.lastName,
        username: values.username,
        password: values.password,
        email: values.userEmail,
      },
    };

    try {
      setIsLoading(true);

      await axiosPrivate.post('web/partners', request);

      showNotification({
        id: 'successfully-created-partner',
        message: 'Successfully created partner',
        icon: <Check size={16} />,
        color: 'teal',
        autoClose: 6000,
      });
      setRefreshTable((prev) => !prev);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-create-partner',
        message: error?.error || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Container fluid>
        <BackButton
          handleClick={() => {
            setComponentManager(OpenComponent.HOME);
          }}
        />
        <Title order={2} my='lg'>
          Partner Registration
        </Title>
        <Divider my='xl' />
        <form onSubmit={handleSubmit}>
          <PartnerDetails
            form={form}
            isAdmin={auth?.role === Roles.ADMINISTRATOR}
          />
          <Divider my='xl' />
          <PartnerResellerUserDetails form={userDetailsForm} />
          <Box
            my='md'
            mt={30}
            style={{ display: 'flex', justifyContent: 'flex-end' }}
          >
            <Button type='submit' loading={isLoading}>
              Submit
            </Button>
          </Box>
        </form>
      </Container>
    </>
  );
}

export default CreatePartnerEntity;

interface Props {
  setComponentManager: React.Dispatch<React.SetStateAction<OpenComponent>>;
  setRefreshTable: React.Dispatch<React.SetStateAction<boolean>>;
}
